import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { registerFighter } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, Mail, Lock, ArrowRight, Eye, EyeOff, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getErrorMessage } from '../utils/errorUtils';

const FighterRegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const { login: authLogin } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await registerFighter({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        invite_code: inviteCode || undefined
      });
      
      // Store token
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(response.data.welcome_message || 'Account created! Welcome to Fighter Portal');
      
      // Redirect to fighter dashboard
      window.location.href = '/fighter/dashboard';
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-red-600/20 border border-red-600/30 rounded-sm flex items-center justify-center">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          
          <h1 className="font-heading text-3xl text-white tracking-tight mb-2">
            FIGHTER PORTAL
          </h1>
          <p className="text-zinc-400">
            Create your account to access your training data
          </p>
          
          {inviteCode && (
            <div className="mt-4 space-y-2">
              <div className="px-4 py-3 bg-green-600/20 border border-green-600/30 rounded-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Zap className="w-5 h-5 text-green-500" />
                  <span className="text-green-400 font-heading uppercase tracking-wide">1 Month Free!</span>
                </div>
                <p className="text-green-400/70 text-xs">
                  You're joining via gym invite - enjoy 1 month on us
                </p>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="pl-10 h-12 bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  data-testid="fighter-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="fighter@email.com"
                  className="pl-10 h-12 bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  data-testid="fighter-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min. 8 characters"
                  className="pl-10 pr-10 h-12 bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  minLength={8}
                  data-testid="fighter-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Confirm password"
                  className="pl-10 h-12 bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  data-testid="fighter-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
              data-testid="fighter-register-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-zinc-800">
            <p className="text-center text-zinc-400">
              Already have an account?{' '}
              <Link to="/fighter/login" className="text-red-500 hover:text-red-400 transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <p className="text-zinc-500 text-sm mb-4">
            {inviteCode 
              ? "🎉 1 month FREE with gym invite • $19.99/month after"
              : "7-day free trial • $19.99/month after"
            }
          </p>
          <Link to="/login" className="text-zinc-400 hover:text-white text-sm transition-colors">
            Are you a gym? Sign in here →
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FighterRegisterPage;
